/**
 * NewSourceFields — the fields of the add-source form for one platform category (demo /form-layouts).
 * The values are described by `new-source-schemas.ts`; `source-payloads.ts` turns them into create requests.
 */
import type React from 'react';
import {
  FormFullWidth,
  FormSection,
  MultiSelectFormField,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
} from '../../../components/form/index.js';
import { Callout } from '../../../components/ui';
import type { PlatformDef } from './platform-catalog.js';
import { CHAIN_OPTIONS, INSTITUTIONAL_TYPES, ON_CHAIN_TAGS } from './platform-catalog.js';

export function NewSourceFields({ platform }: { platform: PlatformDef }): React.ReactNode {
  switch (platform.category) {
    case 'social':
      return (
        <FormSection title="Source" description="One handle, or a list of them: each line becomes its own source.">
          <FormFullWidth>
            <TextFormField name="handle" label="Handle or URL" placeholder="@username or https://..." />
          </FormFullWidth>
          <FormFullWidth>
            <TextAreaFormField
              name="bulkHandles"
              label="Or paste several, one per line"
              rows={4}
              placeholder={'@user1\n@user2\nhttps://...'}
            />
          </FormFullWidth>
        </FormSection>
      );
    case 'on_chain':
      return (
        <FormSection title="Wallet" description="One address, or a list of them: each line becomes its own source.">
          <TextFormField name="address" label="Address" placeholder="0x... or Solana address" />
          <SelectFormField name="chain" label="Chain" options={[...CHAIN_OPTIONS]} required />
          <TextFormField name="label" label="Label" placeholder="Wintermute hot wallet" description="Optional." />
          <MultiSelectFormField name="tags" label="Tags" options={[...ON_CHAIN_TAGS]} placeholder="No tags" />
          <FormFullWidth>
            <TextAreaFormField
              name="bulkAddresses"
              label="Or paste several addresses"
              rows={3}
              placeholder={'0xabc...\n0xdef...'}
            />
          </FormFullWidth>
        </FormSection>
      );
    case 'defi':
      return (
        <FormSection title="Protocol">
          <TextFormField name="protocolName" label="Protocol name" placeholder="Aave" required />
          <MultiSelectFormField
            name="chainFilters"
            label="Chains"
            options={[...CHAIN_OPTIONS]}
            placeholder="All chains"
            description="Track the protocol on these chains only."
          />
        </FormSection>
      );
    case 'institutional':
      return (
        <FormSection title="Institution">
          <TextFormField name="entityName" label="Entity name" placeholder="BlackRock, Citadel" required />
          <TextFormField name="ticker" label="Ticker" placeholder="IBIT, GBTC" description="Optional." />
          <SelectFormField name="entityType" label="Type" options={[...INSTITUTIONAL_TYPES]} required />
        </FormSection>
      );
    case 'research':
    case 'market_infra': {
      const predictionMarket = platform.slug === 'prediction_market';
      return (
        <>
          {predictionMarket && (
            <Callout tone="accent">
              Track Polymarket and Kalshi event contract prices for macro and crypto events.
            </Callout>
          )}
          <FormSection title="Source">
            <TextFormField
              name="sourceName"
              label="Source name"
              required
              placeholder={
                platform.category === 'research'
                  ? 'Messari, Delphi Digital'
                  : predictionMarket
                    ? 'Polymarket'
                    : 'Binance, Coinbase'
              }
            />
            <TextFormField
              name="apiUrl"
              label={platform.category === 'research' ? 'API or RSS URL' : 'RSS or API URL'}
              type="url"
              placeholder="https://..."
              description="Optional."
            />
          </FormSection>
        </>
      );
    }
    default:
      return null;
  }
}
